import React from 'react';
import {
	Alert,
	Modal,
	Table,
	Tag,
	Typography,
	Button,
	Checkbox,
	Tooltip,
	Descriptions,
} from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import {
	RECTANGLE_BAR,
	RECTANGLE_TUBE,
	HEX_BAR,
	PLATE,
	ANGLE,
	CHANNEL,
	ROUND_BAR,
	ROUND_TUBE,
} from '../../../../../config/constants';
import { getEffectiveShapeName } from '../../../../../utils/shapeUtils';
import {
	usePrintMaterialLocationSummaryMutation,
	usePreviewMaterialLocationSummaryMutation,
} from '../../../../../redux/api/material';
import { usePrintLocation } from '../../../../../context/PrintLocationContext';
import { useLocationLabel } from '../../../../../utils/locationLabel';

const { Title, Text } = Typography;

interface MaterialLength {
	id: string;
	friendlyId: string;
	location: string;
	length: number;
	isScrapped?: boolean;
	overflowMessage?: string;
}

interface MaterialInfo {
	type: string;
	width?: number;
	diameter?: number;
	height?: number;
	shape?: string;
	outer_diameter?: number;
	inner_diameter?: number;
	wall_thickness?: number;
}

interface MaterialLengthWithMaterial extends MaterialLength {
	materialInfo?: MaterialInfo;
}

interface MaterialLocationsSummaryModalProps {
	visible: boolean;
	onComplete: () => void;
	materialInfo: MaterialInfo;
	lengths: MaterialLengthWithMaterial[];
	title?: string;
	source?: 'receiving' | 'cutting';
}

const MaterialLocationsSummaryModal: React.FC<
	MaterialLocationsSummaryModalProps
> = ({
	visible,
	onComplete,
	materialInfo,
	lengths,
	title = 'Material Locations Summary',
	source = 'receiving',
}) => {
	const [printMaterialLocationSummary] =
		usePrintMaterialLocationSummaryMutation();
	const [previewMaterialLocationSummary] =
		usePreviewMaterialLocationSummaryMutation();
	const { promptForBuilding } = usePrintLocation();
	const formatLocation = useLocationLabel();

	const [isProcessing, setIsProcessing] = React.useState(false);
	const [isPreviewProcessing, setIsPreviewProcessing] = React.useState(false);
	const [movedStatus, setMovedStatus] = React.useState<{
		[key: string]: boolean;
	}>({});

	React.useEffect(() => {
		if (visible) {
			const initialStatus: { [key: string]: boolean } = {};
			lengths.forEach((length) => {
				initialStatus[length.id] = length.isScrapped || false;
			});
			setMovedStatus(initialStatus);
		}
	}, [visible, lengths]);

	const hasLengths = lengths.length > 0;
	const overflowMessages = Array.from(
		new Set(
			lengths
				.map((length) => length.overflowMessage)
				.filter((message): message is string => Boolean(message))
		)
	);
	const allMoved =
		hasLengths && lengths.every((length) => movedStatus[length.id]);

	const handleMovedChange = (lengthId: string, checked: boolean) => {
		setMovedStatus((prev) => ({
			...prev,
			[lengthId]: checked,
		}));
	};

	const handleCheckAll = (e: CheckboxChangeEvent) => {
		const newStatus: { [key: string]: boolean } = {};
		lengths.forEach((length) => {
			newStatus[length.id] = length.isScrapped ? true : e.target.checked;
		});
		setMovedStatus(newStatus);
	};

	const getMaterialLengthIds = () => lengths.map((l) => l.id);

	const handlePrintComplete = async () => {
		promptForBuilding(
			async (selectedBuildingId) => {
				setIsProcessing(true);
				try {
					await printMaterialLocationSummary({
						material_length_ids: getMaterialLengthIds(),
						source,
						...(selectedBuildingId ? { building_id: selectedBuildingId } : {}),
					}).unwrap();
					onComplete();
				} catch (error) {
					console.error('Print error:', error);
				} finally {
					setIsProcessing(false);
				}
			},
			{ description: 'Select the building to print the material location summary at.' },
		);
	};

	const handlePreview = async () => {
		setIsPreviewProcessing(true);
		try {
			await previewMaterialLocationSummary({
				material_length_ids: getMaterialLengthIds(),
				source,
			}).unwrap();
		} catch (error) {
			console.error('Preview error:', error);
		} finally {
			setIsPreviewProcessing(false);
		}
	};

	const renderSingleMaterialInfo = (info: MaterialInfo) => {
		const {
			type,
			width,
			height,
			shape,
			outer_diameter,
			inner_diameter,
			wall_thickness,
		} = info;
		const shapeName = getEffectiveShapeName(shape) || '';

		return (
			<Descriptions
				size="middle"
				bordered
				column={5}
				className="bg-white"
				styles={{
					label: { fontSize: 16 },
					content: { fontSize: 16 },
				}}
			>
				<Descriptions.Item label="Material Type">
					{type || 'N/A'}
				</Descriptions.Item>

				{(shapeName === RECTANGLE_BAR ||
					shapeName === RECTANGLE_TUBE ||
					shapeName === HEX_BAR ||
					shapeName === PLATE ||
					shapeName === ANGLE ||
					shapeName === CHANNEL) && (
					<Descriptions.Item label="Height">
						{height ? `${height}"` : 'N/A'}
					</Descriptions.Item>
				)}

				{(shapeName === RECTANGLE_BAR ||
					shapeName === RECTANGLE_TUBE ||
					shapeName === PLATE ||
					shapeName === ANGLE ||
					shapeName === CHANNEL) && (
					<Descriptions.Item label="Width">
						{width ? `${width}"` : 'N/A'}
					</Descriptions.Item>
				)}

				{(shapeName === ROUND_BAR || shapeName === ROUND_TUBE) && (
					<Descriptions.Item label="Outer Diameter">
						{outer_diameter ? `${outer_diameter}"` : 'N/A'}
					</Descriptions.Item>
				)}

				{shapeName === ROUND_TUBE && (
					<Descriptions.Item label="Inner Diameter">
						{inner_diameter ? `${inner_diameter}"` : 'N/A'}
					</Descriptions.Item>
				)}

				{(shapeName === RECTANGLE_TUBE ||
					shapeName === ANGLE ||
					shapeName === CHANNEL) && (
					<Descriptions.Item label="Wall Thickness">
						{wall_thickness ? `${wall_thickness}"` : 'N/A'}
					</Descriptions.Item>
				)}
			</Descriptions>
		);
	};

	const getMaterialKey = (info: MaterialInfo) =>
		`${info.type}|${info.shape}|${info.width}|${info.height}|${info.outer_diameter}|${info.inner_diameter}|${info.wall_thickness}`;

	// Group lengths by material type, preserving order
	const groupedByMaterial = React.useMemo(() => {
		const groups: { key: string; info: MaterialInfo; lengths: MaterialLengthWithMaterial[] }[] = [];
		const keyToIndex = new Map<string, number>();

		for (const length of lengths) {
			const info = length.materialInfo ?? materialInfo;
			const key = getMaterialKey(info);
			if (!keyToIndex.has(key)) {
				keyToIndex.set(key, groups.length);
				groups.push({ key, info, lengths: [] });
			}
			groups[keyToIndex.get(key)!].lengths.push(length);
		}
		return groups;
	}, [lengths, materialInfo]);

	const isMixedMaterial = groupedByMaterial.length > 1;

	const sortLengths = (items: MaterialLengthWithMaterial[]) => {
		return [...items].sort((a, b) => {
			if (a.isScrapped && !b.isScrapped) return 1;
			if (!a.isScrapped && b.isScrapped) return -1;
			const aIsMoved = movedStatus[a.id] || false;
			const bIsMoved = movedStatus[b.id] || false;
			if (!aIsMoved && bIsMoved) return -1;
			if (aIsMoved && !bIsMoved) return 1;
			return 0;
		});
	};

	const columns = [
		{
			title: <span style={{ fontSize: 16 }}>ID</span>,
			dataIndex: 'friendlyId',
			key: 'friendlyId',
			width: 120,
			render: (text: string) => <Text strong style={{ fontSize: 16 }}>{text}</Text>,
		},
		{
			title: <span style={{ fontSize: 16 }}>Location</span>,
			dataIndex: 'location',
			key: 'location',
			width: 150,
			render: (text: string) => <Text style={{ fontSize: 16 }}>{formatLocation(text)}</Text>,
		},
		{
			title: <span style={{ fontSize: 16 }}>Length</span>,
			dataIndex: 'length',
			key: 'length',
			width: 120,
			align: 'right' as const,
			render: (length: number) => (
				<Text style={{ fontVariantNumeric: 'tabular-nums' }}>
					{length?.toFixed(3) || '0.000'}
				</Text>
			),
		},
		{
			title: () => (
				<div className="flex items-center gap-2">
					<span style={{ fontSize: 16 }}>Moved</span>
					<Checkbox
						checked={allMoved}
						indeterminate={!allMoved && lengths.some((l) => movedStatus[l.id])}
						onChange={handleCheckAll}
					/>
				</div>
			),
			dataIndex: 'moved',
			key: 'moved',
			width: 150,
			render: (_: any, record: MaterialLength) => {
				const isMoved = movedStatus[record.id] || false;

				return (
					<div
						className="flex items-center gap-2"
						onClick={(e) => e.stopPropagation()}
					>
						<Checkbox
							checked={isMoved}
							disabled={record.isScrapped}
							onChange={(e) => handleMovedChange(record.id, e.target.checked)}
						/>
						{record.isScrapped ? (
							<Tag style={{ fontSize: 16 }} color="error">Scrapped</Tag>
						) : (
							isMoved && <Tag style={{ fontSize: 16 }} color="success">Moved</Tag>
						)}
					</div>
				);
			},
		},
	];

	const renderGroupTable = (items: MaterialLengthWithMaterial[]) => (
		<Table
			columns={columns}
			dataSource={sortLengths(items)}
			rowKey="id"
			pagination={false}
			size="middle"
			bordered
			scroll={isMixedMaterial ? undefined : { y: 350 }}
			onRow={(record) => ({
				onClick: () => {
					if (!record.isScrapped) {
						handleMovedChange(record.id, !movedStatus[record.id]);
					}
				},
				style: { cursor: record.isScrapped ? 'default' : 'pointer' },
			})}
			rowClassName={(record) => {
				if (record.isScrapped) return 'bg-red-50 text-gray-400';
				if (movedStatus[record.id])
					return 'bg-gray-50 text-gray-400 transition-colors';
				return 'transition-colors hover:bg-blue-50';
			}}
		/>
	);

	return (
		<Modal
			title={
				<Title level={3} style={{ margin: 0 }}>
					{title}
				</Title>
			}
			open={visible}
			onCancel={onComplete}
			closable={!hasLengths}
			maskClosable={!hasLengths}
			keyboard={!hasLengths}
			width={850}
			centered
			footer={
				<div className="flex justify-end gap-3 mt-4">
					{hasLengths ? (
						<>
							<Tooltip title={!allMoved ? 'You have not moved all lengths.' : ''}>
								<span>
									<Button
										onClick={handlePreview}
										loading={isPreviewProcessing}
										disabled={!allMoved}
										style={!allMoved ? { pointerEvents: 'none' } : {}}
										size="large"
									>
										Preview
									</Button>
								</span>
							</Tooltip>
							<Tooltip title={!allMoved ? 'You have not moved all lengths.' : ''}>
								<span>
									<Button
										onClick={handlePrintComplete}
										loading={isProcessing}
										disabled={!allMoved}
										style={!allMoved ? { pointerEvents: 'none' } : {}}
										size="large"
									>
										Print Complete
									</Button>
								</span>
							</Tooltip>
							<Tooltip title={!allMoved ? 'You have not moved all lengths.' : ''}>
								<span>
									<Button
										type="primary"
										onClick={onComplete}
										disabled={!allMoved}
										style={!allMoved ? { pointerEvents: 'none' } : {}}
										size="large"
									>
										Complete
									</Button>
								</span>
							</Tooltip>
						</>
					) : (
						<Button type="primary" onClick={onComplete} size="large">
							Close
						</Button>
					)}
				</div>
			}
		>
			<div className="flex flex-col pt-2 pb-2" style={isMixedMaterial ? { maxHeight: 500, overflowY: 'auto' } : undefined}>
				{overflowMessages.map((message) => (
					<Alert
						key={message}
						type="warning"
						showIcon
						message="Sent to Overflow"
						description={message}
						style={{ marginBottom: 12 }}
					/>
				))}
				{!hasLengths ? (
					<Text type="secondary" style={{ fontSize: 16 }}>No material lengths to display.</Text>
				) : isMixedMaterial ? (
					groupedByMaterial.map((group, idx) => (
						<div key={group.key} style={{ marginBottom: idx < groupedByMaterial.length - 1 ? 20 : 0 }}>
							<div className="mb-2">
								{renderSingleMaterialInfo(group.info)}
							</div>
							<div className="flex justify-between items-end mb-2">
								<Text strong style={{ fontSize: 16 }}>Lengths ({group.lengths.length}):</Text>
							</div>
							{renderGroupTable(group.lengths)}
						</div>
					))
				) : (
					<>
						<div className="mb-6">
							{renderSingleMaterialInfo(materialInfo)}
						</div>
						<div className="flex justify-between items-end mb-2">
							<Text strong style={{ fontSize: 16 }}>Material Lengths ({lengths.length}):</Text>
						</div>
						{renderGroupTable(lengths)}
					</>
				)}
			</div>
		</Modal>
	);
};

export default MaterialLocationsSummaryModal;
